import { Fragment, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { __TOPONYMY_API_URL__ } from 'shell/constants/environment';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import FormCirculationArea, { CirculationAreaForm } from 'system/components/toponymy/form';
import { CommonToponymyCrumbs } from '../constants';
import { toast } from 'react-toastify';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import * as yup from 'yup';
import { HttpResponse } from 'shell/models/http-response';
import { DateTime } from 'luxon';
import { regexOnlyNumber } from 'shell/constants/regex';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { CirculationArea } from 'shell/models/circulation-area';

const validationSchema = yup.object().shape({
  estabilishmentDate: yup
    .date()
    .nullable()
    .required('Campo obbligatorio')
    .typeError('Data non valida')
    .max(DateTime.now(), 'Non può essere maggiore di oggi'),
  name: yup.string().required('Campo obbligatorio'),
  code: yup.string().matches(regexOnlyNumber, 'Campo numerico').required('Campo obbligatorio'),
  type: yup.string().required('Campo obbligatorio'),
  typeDeliberated: yup.string().required('Campo obbligatorio'),
});

const CirculationAreaNewPage = (): React.ReactElement => {
  const insertCirculationArea = useHttpClient<HttpResponse<CirculationArea>>(
    `${__TOPONYMY_API_URL__}/areaCircolazione`,
    {
      options: {
        method: 'POST',
        errorCatching: true,
        loading: true,
      },
    }
  );

  const { userProfile } = useUserProfile();

  const navigate = useNavigate();

  const onSubmit = useCallback(
    (formValues?: CirculationAreaForm) => {
      (
        insertCirculationArea({
          data: {
            descrizionePostale: formValues?.name,
            descrizioneRidotta: formValues?.name.split(' ').slice(1).join(' '),
            descrizionePostaleRidotta: formValues
              ? `${formValues?.type} ${formValues?.name.split(' ').slice(1).join(' ')}`
              : null,
            dataIstituzione: formValues?.estabilishmentDate?.toString().replace(/-/g, ''),
            dataCessazione: null,
            flag: formValues?.deliberated ? 'R' : '',
            sottotitolo: '',
            incrementoNumerazione: 1,
            tipoArea: formValues?.type,
            tipoDelibera: formValues?.typeDeliberated,
            key: {
              codice: formValues?.code,
              ente: userProfile.datiLogin?.key?.codiceEnte,
            },
          },
        }) as ResponsePromise<HttpResponse<CirculationArea>>
      ).then((response) => {
        const payload = response?.data.payload;

        const { key } = payload || {};

        const codice = key?.codice || {};

        toast.success('Operazione eseguita con successo');

        navigate(`/sistema/toponomastica/area-di-circolazione/modifica/${codice}`);
      });
    },
    [insertCirculationArea, navigate]
  );

  return (
    <Fragment>
      <Breadcrumbs items={[...CommonToponymyCrumbs, { name: 'Nuova Area di circolazione' }]} />
      <FormCirculationArea
        title="Nuova Area di circolazione"
        onOk={onSubmit}
        onCancel={() => {
          navigate('/sistema/toponomastica/area-di-circolazione');
        }}
        newArea
        validationSchema={validationSchema}
      />
    </Fragment>
  );
};

CirculationAreaNewPage.displayName = 'Circulation Area new page component';

export default CirculationAreaNewPage;
